import React, { useState, useRef, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Download, Upload, FileSpreadsheet, Loader2, CheckCircle2, XCircle,
  AlertTriangle, ChevronDown, ChevronRight, Info, RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import api, { formatErrorMessage } from '../utils/api';
import { esGestorComercial } from '../utils/permissions';

const fmtCLP = (n) => '$' + Math.round(Number(n || 0)).toLocaleString('es-CL');

export default function ImportVentasTab({ currentUser }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedErrors, setExpandedErrors] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);
  const autorizado = esGestorComercial(currentUser);

  const descargarTemplate = async () => {
    try {
      const r = await api.get('/import/ventas/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url; a.download = 'plantilla_ventas_historicas.xlsx'; a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 1000);
      toast.success('Plantilla descargada');
    } catch (err) { toast.error(formatErrorMessage(err)); }
  };

  const [reparando, setReparando] = useState(false);
  const repararImportaciones = async () => {
    if (!window.confirm('¿Reparar PSPs importadas con status de cuota inválido? Esta acción corrige datos históricos y es idempotente.')) return;
    setReparando(true);
    try {
      const r = await api.post('/import/ventas/reparar');
      if (r.data.psps_reparadas === 0) {
        toast.info('No se encontraron PSPs que reparar. Todo está OK.');
      } else {
        toast.success(`Reparadas ${r.data.psps_reparadas} PSP(s) · ${r.data.cuotas_reparadas} cuota(s) actualizadas.`);
      }
    } catch (err) { toast.error(formatErrorMessage(err)); }
    finally { setReparando(false); }
  };

  const onPick = (f) => {
    if (!f) return;
    if (!/\.xlsx?$/i.test(f.name)) { toast.error('Solo archivos .xlsx'); return; }
    setFile(f); setPreview(null); setResult(null);
  };

  const previsualizar = async () => {
    if (!file) { toast.error('Selecciona un archivo'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/import/ventas/preview', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(r.data);
      const errs = r.data.con_errores;
      if (errs > 0) toast.warning(`${errs} fila(s) con errores. Revisa antes de importar.`);
      else toast.success(`${r.data.total} fila(s) válidas listas para importar.`);
    } catch (err) { toast.error(formatErrorMessage(err)); }
    finally { setLoading(false); }
  };

  const ejecutarImport = async () => {
    if (!file || !preview) return;
    if (preview.con_errores > 0) { toast.error('Corrige los errores antes de importar'); return; }
    if (!window.confirm(`¿Importar ${preview.validas} PSPs? Esta acción no se puede deshacer.`)) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/import/ventas', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      toast.success(`${r.data.psps_creadas} PSPs importadas exitosamente`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && detail.errores) {
        setResult({ ok: false, errores_import: detail.errores, mensaje: detail.message });
        toast.error(detail.message);
      } else {
        toast.error(formatErrorMessage(err));
      }
    } finally { setImporting(false); }
  };

  const reset = () => {
    setFile(null); setPreview(null); setResult(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const filasConError = useMemo(
    () => (preview?.filas || []).filter(f => !f.ok),
    [preview]
  );

  if (!autorizado) {
    return (
      <Card className="p-8 text-center border-amber-200 bg-amber-50">
        <AlertTriangle className="w-10 h-10 text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-amber-800">
          Solo Socios o Líder Comercial pueden importar ventas históricas.
        </p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="import-ventas-tab">
      {/* Header + template */}
      <Card className="p-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
              <FileSpreadsheet className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-sm font-bold text-slate-900">Importación de Ventas Históricas</h3>
              <p className="text-xs text-slate-500">
                Carga masiva de PSPs desde Excel. Crea clientes nuevos si no existen y marca las PSPs como <code className="text-[10px] bg-slate-100 px-1 rounded">importado: true</code>.
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={repararImportaciones} disabled={reparando} data-testid="import-reparar-btn" title="Corrige PSPs importadas históricas con datos inválidos que rompían el listado de ventas">
              {reparando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
              Reparar históricas
            </Button>
            <Button variant="outline" onClick={descargarTemplate} data-testid="import-template-btn">
              <Download className="w-4 h-4 mr-1" /> Descargar plantilla
            </Button>
          </div>
        </div>
      </Card>

      {/* Instrucciones */}
      <Card className="p-3 bg-sky-50 border-sky-200">
        <div className="flex items-start gap-2 text-xs text-sky-900">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold mb-1">Flujo recomendado:</p>
            <ol className="list-decimal list-inside space-y-0.5">
              <li>Descarga la plantilla y complétala respetando las columnas obligatorias (marcadas con *).</li>
              <li>Sube el archivo → obtén una vista previa con validación fila por fila.</li>
              <li>Si hay errores, corrígelos en el Excel y vuelve a subirlo.</li>
              <li>Cuando el preview esté 100% verde, presiona <strong>&ldquo;Importar N PSPs&rdquo;</strong>.</li>
            </ol>
          </div>
        </div>
      </Card>

      {/* Dropzone */}
      {!result && (
        <Card className="p-4">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); onPick(e.dataTransfer?.files?.[0]); }}
            onClick={() => inputRef.current?.click()}
            className={`cursor-pointer border-2 border-dashed rounded-lg p-6 text-center transition ${drag ? 'border-emerald-500 bg-emerald-50' : 'border-slate-300 bg-slate-50 hover:bg-slate-100'}`}
            data-testid="import-dropzone"
          >
            <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            {file ? (
              <div>
                <div className="text-sm font-semibold text-slate-800">{file.name}</div>
                <div className="text-[11px] text-slate-500">{(file.size / 1024).toFixed(1)} KB</div>
              </div>
            ) : (
              <div className="text-xs text-slate-500">
                Arrastra el .xlsx aquí o haz clic para seleccionar
              </div>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx"
              className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="import-file-input"
            />
          </div>
          <div className="mt-3 flex justify-end gap-2">
            {file && !preview && (
              <Button
                onClick={previsualizar}
                disabled={loading}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="import-preview-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                Vista previa
              </Button>
            )}
          </div>
        </Card>
      )}

      {/* Preview */}
      {preview && !result && (
        <Card className="p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs flex-wrap">
              <Badge className="bg-slate-100 text-slate-700">{preview.total} filas</Badge>
              <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-0.5" />{preview.validas} válidas</Badge>
              <Badge className="bg-rose-100 text-rose-700"><XCircle className="w-3 h-3 mr-0.5" />{preview.con_errores} con errores</Badge>
              <Badge className="bg-indigo-100 text-indigo-700">{preview.clientes_nuevos} clientes nuevos</Badge>
              <Badge className="bg-slate-100 text-slate-700">{preview.clientes_reusados} clientes reusados</Badge>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset} data-testid="import-reset-btn">
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Cambiar archivo
              </Button>
              <Button
                onClick={ejecutarImport}
                disabled={importing || preview.con_errores > 0 || preview.validas === 0}
                className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40"
                data-testid="import-execute-btn"
              >
                {importing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Importar {preview.validas} PSPs
              </Button>
            </div>
          </div>

          {/* Errores expandibles */}
          {filasConError.length > 0 && (
            <div className="mb-3">
              <button
                onClick={() => setExpandedErrors(v => !v)}
                className="flex items-center gap-1 text-xs text-rose-700 hover:text-rose-900"
                data-testid="import-errors-toggle"
              >
                {expandedErrors ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Ver los {filasConError.length} errores detallados
              </button>
              {expandedErrors && (
                <div className="mt-2 p-2 rounded bg-rose-50 border border-rose-200 text-[11px] space-y-1 max-h-48 overflow-auto">
                  {filasConError.map(f => (
                    <div key={f.fila} className="flex gap-2">
                      <Badge className="bg-rose-200 text-rose-800 h-4 text-[9px]">Fila {f.fila}</Badge>
                      <span className="text-rose-900">{f.errores.join(' · ')}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="text-left px-2 py-1">Fila</th>
                  <th className="text-left px-2 py-1">Estado</th>
                  <th className="text-left px-2 py-1">Cliente</th>
                  <th className="text-left px-2 py-1">Título PSP</th>
                  <th className="text-right px-2 py-1">Monto</th>
                  <th className="text-center px-2 py-1">Cuotas</th>
                  <th className="text-left px-2 py-1">Estado PSP</th>
                  <th className="text-left px-2 py-1">Área</th>
                </tr>
              </thead>
              <tbody>
                {(preview.filas || []).map(f => (
                  <tr
                    key={f.fila}
                    className={`border-t border-slate-100 ${f.ok ? 'hover:bg-emerald-50' : 'bg-rose-50 hover:bg-rose-100'}`}
                    data-testid={`import-row-${f.fila}`}
                  >
                    <td className="px-2 py-1 text-slate-500">{f.fila}</td>
                    <td className="px-2 py-1">
                      {f.ok ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                      ) : (
                        <span title={f.errores.join(' · ')}>
                          <XCircle className="w-3.5 h-3.5 text-rose-600" />
                        </span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      {f.data?.cliente_nombre || '—'}
                      {f.cliente_action === 'crear' && (
                        <span className="ml-1 text-[9px] text-indigo-700 bg-indigo-100 px-1 rounded">nuevo</span>
                      )}
                      {f.cliente_action === 'reusar' && (
                        <span className="ml-1 text-[9px] text-slate-600 bg-slate-100 px-1 rounded">existente</span>
                      )}
                    </td>
                    <td className="px-2 py-1">{f.data?.titulo || '—'}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{fmtCLP(f.data?.precio_total)}</td>
                    <td className="px-2 py-1 text-center">{f.data?.num_cuotas || '—'}</td>
                    <td className="px-2 py-1">{f.data?.status || '—'}</td>
                    <td className="px-2 py-1">{f.data?.area || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Resultado */}
      {result && (
        <Card className="p-4">
          {result.ok ? (
            <>
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div>
                  <h3 className="text-base font-bold text-slate-900">Importación completada</h3>
                  <p className="text-xs text-slate-500">Todas las PSPs quedan marcadas con <code className="text-[10px]">importado: true</code></p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center mb-4">
                <div className="p-3 rounded bg-emerald-50 border border-emerald-200">
                  <div className="text-2xl font-bold text-emerald-700 tabular-nums">{result.psps_creadas}</div>
                  <div className="text-[11px] text-emerald-800">PSPs importadas</div>
                </div>
                <div className="p-3 rounded bg-indigo-50 border border-indigo-200">
                  <div className="text-2xl font-bold text-indigo-700 tabular-nums">{result.clientes_creados}</div>
                  <div className="text-[11px] text-indigo-800">Clientes creados</div>
                </div>
                <div className={`p-3 rounded ${(result.errores || []).length > 0 ? 'bg-rose-50 border border-rose-200' : 'bg-slate-50 border border-slate-200'}`}>
                  <div className={`text-2xl font-bold tabular-nums ${(result.errores || []).length > 0 ? 'text-rose-700' : 'text-slate-500'}`}>{(result.errores || []).length}</div>
                  <div className={`text-[11px] ${(result.errores || []).length > 0 ? 'text-rose-800' : 'text-slate-500'}`}>Errores de ejecución</div>
                </div>
              </div>
              {(result.errores || []).length > 0 && (
                <div className="p-2 rounded bg-rose-50 border border-rose-200 text-[11px] space-y-1 mb-3">
                  {result.errores.map((e, i) => (
                    <div key={i}><strong>Fila {e.fila}:</strong> {e.error}</div>
                  ))}
                </div>
              )}
              <div className="flex justify-end">
                <Button variant="outline" onClick={reset} data-testid="import-nuevo-btn">
                  <RefreshCw className="w-4 h-4 mr-1" /> Iniciar nueva importación
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 mb-3">
                <XCircle className="w-6 h-6 text-rose-600" />
                <div>
                  <h3 className="text-base font-bold text-rose-900">Import rechazado</h3>
                  <p className="text-xs text-slate-500">{result.mensaje}</p>
                </div>
              </div>
              {(result.errores_import || []).length > 0 && (
                <div className="p-2 rounded bg-rose-50 border border-rose-200 text-[11px] space-y-1 mb-3 max-h-48 overflow-auto">
                  {result.errores_import.map((e, i) => (
                    <div key={i}><strong>Fila {e.fila}:</strong> {e.errores.join(' · ')}</div>
                  ))}
                </div>
              )}
              <Button variant="outline" onClick={reset}>
                <RefreshCw className="w-4 h-4 mr-1" /> Volver a intentar
              </Button>
            </>
          )}
        </Card>
      )}
    </div>
  );
}
